// 카탈로그 도메인(카테고리/상품/배너/찜) 공통 타입과 zod 스키마.
// 사용자 앱/관리자 콘솔/API 가 동일한 검증 규칙을 공유하도록 본 파일에 모은다.
// 응답 DTO 는 별도 응답 파일에 두지 않고, 본 파일의 타입을 그대로 활용하는 것을 원칙으로 한다.

import { z } from "zod";
import { productUnitSchema } from "./schemas.js";

// 배너 링크 종류.
// 사용자 메인 화면에서 배너 클릭 시 동작을 분기한다.
//  - product : 특정 상품 상세로 이동 (linkTargetId 필요)
//  - category : 특정 카테고리 목록으로 이동 (linkTargetId 필요)
//  - external : 외부 URL 새 창 (linkUrl 필요)
//  - none : 클릭 무동작
export const BANNER_LINK_KIND = {
  PRODUCT: "product",
  CATEGORY: "category",
  EXTERNAL: "external",
  NONE: "none",
} as const;

export type BannerLinkKind = (typeof BANNER_LINK_KIND)[keyof typeof BANNER_LINK_KIND];

export const bannerLinkKindSchema = z.enum([
  BANNER_LINK_KIND.PRODUCT,
  BANNER_LINK_KIND.CATEGORY,
  BANNER_LINK_KIND.EXTERNAL,
  BANNER_LINK_KIND.NONE,
]) satisfies z.ZodType<BannerLinkKind>;

// 카테고리 이름 최대 길이.
// UI 카드 그리드에서 두 줄을 넘기지 않도록 어느 정도 제약을 둔다 (UI 가이드에 맞춰 후속 조정 가능).
const CATEGORY_NAME_MAX = 30;
const PRODUCT_NAME_MAX = 80;
const PRODUCT_ORIGIN_MAX = 60;
const PRODUCT_DESCRIPTION_MAX = 2000;
const IMAGE_KEY_MAX = 256;
// 추가 옵션 문구 최대 길이. 손님 모달/영수증(42칸) 양쪽에서 한 줄에 담기도록 짧게 제한한다.
const PRODUCT_OPTION_LABEL_MAX = 40;
// 상품당 추가 옵션 최대 개수. 선택 UI 가 길어지지 않도록 제한.
export const PRODUCT_OPTION_MAX = 20;
// 옵션 1개당 추가 중량 상한(g). 오입력(자릿수 실수) 방지용 느슨한 상한.
const PRODUCT_OPTION_GRAMS_MAX = 100_000;

// 관리자: 카테고리 생성/수정 입력.
// sort_order/is_active 는 PATCH 에서 부분 갱신 가능하도록 별도 스키마(아래)에서 partial 로 정의한다.
export const categoryCreateInputSchema = z.object({
  name: z.string().trim().min(1, "카테고리 이름을 입력하세요.").max(CATEGORY_NAME_MAX),
  iconImageKey: z.string().trim().max(IMAGE_KEY_MAX).nullable().optional(),
  sortOrder: z.number().int().min(0).optional(),
  isActive: z.boolean().optional(),
});

export type CategoryCreateInput = z.infer<typeof categoryCreateInputSchema>;

export const categoryUpdateInputSchema = categoryCreateInputSchema.partial();
export type CategoryUpdateInput = z.infer<typeof categoryUpdateInputSchema>;

// 관리자: 카테고리 정렬 일괄 변경 입력.
// 드래그 정렬 UX 를 가정해 (id, sortOrder) 쌍 배열로 전달한다.
export const categoryReorderInputSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.string().min(1),
        sortOrder: z.number().int().min(0),
      }),
    )
    .min(1),
});
export type CategoryReorderInput = z.infer<typeof categoryReorderInputSchema>;

// 공통 카테고리 응답 DTO.
// 사용자 화면은 is_active = 1 만 받으며, 관리자 화면은 모두 받는다.
export interface CategoryDto {
  id: string;
  name: string;
  iconImageKey: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// 관리자: 상품 추가 옵션 입력(예: "100g 추가" / 100g / 3,600원).
//  - addGrams 는 실중량 정산에 합산되는 추가 중량이다. 중량 없는 부가옵션이면 0.
//  - 상세 이미지와 마찬가지로 저장 시 기존 행을 지우고 배열 순서대로 재삽입한다.
export const productOptionInputSchema = z.object({
  label: z.string().trim().min(1, "옵션 문구를 입력하세요.").max(PRODUCT_OPTION_LABEL_MAX),
  addGrams: z.number().int().min(0).max(PRODUCT_OPTION_GRAMS_MAX),
  addPriceKrw: z.number().int().min(0),
});
export type ProductOptionInput = z.infer<typeof productOptionInputSchema>;

// 관리자: 상품 생성 입력.
// 상세 이미지(product_images) 는 별도 엔드포인트가 아닌 본 입력에 imageKeys 배열로 함께 전달한다.
//  - 등록/수정 시 서버는 기존 행을 모두 지우고 본 배열 순서로 재삽입한다 (idempotent 단순화).
export const productCreateInputSchema = z.object({
  // 기본(대표) 카테고리. 상세/정렬의 기준이 된다.
  categoryId: z.string().min(1),
  // 이 상품이 노출되는 전체 카테고리 집합(개선항목 11). 대표 카테고리를 포함하며, 미지정 시 [categoryId] 로 간주.
  categoryIds: z.array(z.string().min(1)).min(1).max(8).optional(),
  name: z.string().trim().min(1).max(PRODUCT_NAME_MAX),
  priceKrw: z.number().int().min(0),
  unit: productUnitSchema,
  // 단위가 '팩'일 때 1팩 중량(g). 비우면 null(미설정). 팩이 아니면 무시한다.
  packWeightG: z.number().int().positive().max(1_000_000).nullable().optional(),
  // 실중량 정산 대상 여부(PR-B). 무게가변 단위(100g/근/kg) 상품은 준비완료 때 실중량으로 최종가를 확정한다.
  //  - 관리자 폼은 단위의 weight_variable 기본값을 따라 자동 체크하되, 해제할 수 있다.
  weightAdjustable: z.boolean().optional(),
  origin: z.string().trim().max(PRODUCT_ORIGIN_MAX).nullable().optional(),
  description: z.string().max(PRODUCT_DESCRIPTION_MAX).nullable().optional(),
  mainImageKey: z.string().trim().max(IMAGE_KEY_MAX).nullable().optional(),
  detailImageKeys: z.array(z.string().trim().min(1).max(IMAGE_KEY_MAX)).max(20).optional(),
  // 추가 옵션 목록(선택). 손님이 담을 때 개수를 고르며, 중량/금액이 라인에 합산된다.
  options: z.array(productOptionInputSchema).max(PRODUCT_OPTION_MAX).optional(),
  isSoldOut: z.boolean().optional(),
  isVisible: z.boolean().optional(),
  isBest: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});
export type ProductCreateInput = z.infer<typeof productCreateInputSchema>;

export const productUpdateInputSchema = productCreateInputSchema.partial();
export type ProductUpdateInput = z.infer<typeof productUpdateInputSchema>;

// 관리자: 상품 일괄 처리 입력.
// 가격 일괄 수정과 품절 일괄 토글 두 가지를 동일 엔드포인트에서 다룬다.
// 둘 중 하나만 보낼 수 있도록 union 으로 정의한다.
export const productBulkPatchInputSchema = z.object({
  productIds: z.array(z.string().min(1)).min(1).max(500),
  patch: z.union([
    z.object({ priceKrw: z.number().int().min(0) }),
    z.object({ isSoldOut: z.boolean() }),
    z.object({ isVisible: z.boolean() }),
    z.object({ isBest: z.boolean() }),
  ]),
});
export type ProductBulkPatchInput = z.infer<typeof productBulkPatchInputSchema>;

// 진열 순서 변경(관리자 드래그). scope 별로 갱신 대상이 다르다.
//  - best     : products.best_sort_order (메인 베스트 목록 순서)
//  - all      : products.sort_order (메인 전체 상품 목록 순서)
//  - category : product_categories.sort_order (해당 카테고리 진열 순서, categoryId 필수)
// orderedIds 는 화면에 보이는 순서대로의 상품 id 배열. 배열 인덱스가 곧 진열 순서가 된다.
export const PRODUCT_REORDER_SCOPE = ["best", "all", "category"] as const;
export type ProductReorderScope = (typeof PRODUCT_REORDER_SCOPE)[number];

export const productReorderInputSchema = z
  .object({
    scope: z.enum(PRODUCT_REORDER_SCOPE),
    categoryId: z.string().min(1).optional(),
    orderedIds: z.array(z.string().min(1)).max(2000),
  })
  .refine((v) => v.scope !== "category" || !!v.categoryId, {
    message: "카테고리 진열 순서 변경에는 categoryId 가 필요합니다.",
    path: ["categoryId"],
  });
export type ProductReorderInput = z.infer<typeof productReorderInputSchema>;

// 관리자: 배너 노출 순서 일괄 변경 입력.
// orderedIds 는 화면에 보이는 순서대로의 배너 id 배열. 배열 인덱스가 곧 sort_order 가 된다.
export const bannerReorderInputSchema = z.object({
  orderedIds: z.array(z.string().min(1)).max(500),
});
export type BannerReorderInput = z.infer<typeof bannerReorderInputSchema>;

// 상품 추가 옵션 응답 DTO. 손님 선택 모달과 관리자 폼이 함께 쓴다.
export interface ProductOptionDto {
  id: string;
  label: string;
  // 이 옵션 1개당 추가 중량(g). 실중량 정산에 합산된다. 중량 없는 옵션이면 0.
  addGrams: number;
  // 이 옵션 1개당 추가 금액(원).
  addPriceKrw: number;
  sortOrder: number;
}

// 공통 상품 응답 DTO.
// 사용자 응답은 is_visible = 1 만 노출, is_sold_out 은 그대로 내려 클라이언트가 회색 처리한다.
export interface ProductDto {
  id: string;
  // 기본(대표) 카테고리.
  categoryId: string;
  // 이 상품이 노출되는 전체 카테고리 id 집합(대표 포함). 목록 응답에서는 [categoryId] 만 채워질 수 있다.
  categoryIds: string[];
  name: string;
  priceKrw: number;
  unit: string;
  // 단위가 '팩'일 때 1팩 중량(g). 미설정이면 null.
  packWeightG: number | null;
  // 실중량 정산 대상 여부(PR-B).
  weightAdjustable: boolean;
  origin: string | null;
  description: string | null;
  mainImageKey: string | null;
  detailImageKeys: string[];
  // 추가 옵션 목록(없으면 빈 배열). 비어 있으면 손님 앱은 기존 즉시 담기(수량 스테퍼)로 동작한다.
  options: ProductOptionDto[];
  isSoldOut: boolean;
  isVisible: boolean;
  isBest: boolean;
  salesCount: number;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

// 관리자: 배너 생성/수정 입력.
// link_kind 별 필수 필드 조합을 zod refine 으로 검증한다.
//  - product/category : linkTargetId 필수
//  - external : linkUrl 필수
//  - none : 모두 무시
export const bannerCreateInputSchema = z
  .object({
    imageKey: z.string().trim().min(1).max(IMAGE_KEY_MAX),
    linkKind: bannerLinkKindSchema,
    linkTargetId: z.string().min(1).nullable().optional(),
    linkUrl: z.string().url().nullable().optional(),
    startsAt: z.string().datetime().nullable().optional(),
    endsAt: z.string().datetime().nullable().optional(),
    sortOrder: z.number().int().min(0).optional(),
    isActive: z.boolean().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.linkKind === BANNER_LINK_KIND.PRODUCT || value.linkKind === BANNER_LINK_KIND.CATEGORY) {
      if (!value.linkTargetId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["linkTargetId"],
          message: "연결 대상 ID 가 필요합니다.",
        });
      }
    }
    if (value.linkKind === BANNER_LINK_KIND.EXTERNAL && !value.linkUrl) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["linkUrl"],
        message: "외부 링크 URL 이 필요합니다.",
      });
    }
    if (value.startsAt && value.endsAt) {
      if (new Date(value.startsAt).getTime() >= new Date(value.endsAt).getTime()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["endsAt"],
          message: "노출 종료 시각은 시작 시각보다 뒤여야 합니다.",
        });
      }
    }
  });

export type BannerCreateInput = z.infer<typeof bannerCreateInputSchema>;

// 배너 수정 입력.
// superRefine 가 걸린 객체는 .partial() 이 직접 호출되지 않으므로, 내부 base 객체를 partial 화한 뒤 동일한 refine 을 다시 건다.
const bannerBaseSchema = z.object({
  imageKey: z.string().trim().min(1).max(IMAGE_KEY_MAX),
  linkKind: bannerLinkKindSchema,
  linkTargetId: z.string().min(1).nullable().optional(),
  linkUrl: z.string().url().nullable().optional(),
  startsAt: z.string().datetime().nullable().optional(),
  endsAt: z.string().datetime().nullable().optional(),
  sortOrder: z.number().int().min(0).optional(),
  isActive: z.boolean().optional(),
});

export const bannerUpdateInputSchema = bannerBaseSchema.partial().superRefine((value, ctx) => {
  if (
    value.linkKind &&
    (value.linkKind === BANNER_LINK_KIND.PRODUCT || value.linkKind === BANNER_LINK_KIND.CATEGORY) &&
    value.linkTargetId === null
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["linkTargetId"],
      message: "연결 대상 ID 가 필요합니다.",
    });
  }
  if (value.linkKind === BANNER_LINK_KIND.EXTERNAL && value.linkUrl === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["linkUrl"],
      message: "외부 링크 URL 이 필요합니다.",
    });
  }
  if (value.startsAt && value.endsAt) {
    if (new Date(value.startsAt).getTime() >= new Date(value.endsAt).getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["endsAt"],
        message: "노출 종료 시각은 시작 시각보다 뒤여야 합니다.",
      });
    }
  }
});

export type BannerUpdateInput = z.infer<typeof bannerUpdateInputSchema>;

// 공통 배너 응답 DTO.
export interface BannerDto {
  id: string;
  imageKey: string;
  linkKind: BannerLinkKind;
  linkTargetId: string | null;
  linkUrl: string | null;
  startsAt: string | null;
  endsAt: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// 사용자 상품 목록 쿼리.
// categoryId 또는 q(검색어) 중 하나, 둘 다 또는 모두 비울 수 있다 (모두 빈 경우 최신순 전체).
// 상품 목록 정렬 기준.
//  - popular: 베스트 우선 + 판매량 (메인 기본)
//  - sales  : 판매량순
//  - price  : 가격 오름차순
export const PRODUCT_SORT = ["popular", "sales", "price"] as const;
export type ProductSort = (typeof PRODUCT_SORT)[number];

export const productListQuerySchema = z.object({
  categoryId: z.string().min(1).optional(),
  q: z.string().trim().min(1).max(60).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).optional(),
  sort: z.enum(PRODUCT_SORT).optional(),
  // 품절 상품 제외 여부. 쿼리스트링에서는 "1" 로 전달한다(메인 전체 상품 목록에서 사용).
  excludeSoldOut: z.enum(["1"]).optional(),
});
export type ProductListQuery = z.infer<typeof productListQuerySchema>;

// 이미지 업로드 사전 검증.
// 클라이언트가 보내는 메타데이터로, 실제 바이트 검증은 서버에서 별도로 수행한다 (MIME sniff + 용량 체크).
export const IMAGE_UPLOAD_MAX_BYTES = 5 * 1024 * 1024; // 5MB
export const IMAGE_UPLOAD_ALLOWED_MIME = [
  "image/jpeg",
  "image/png",
  "image/webp",
] as const;
export type ImageUploadMime = (typeof IMAGE_UPLOAD_ALLOWED_MIME)[number];

// 이미지 업로드 카테고리.
// R2 객체 키 prefix 로 사용된다 (categories/products/banners/products-detail).
export const IMAGE_UPLOAD_KIND = {
  CATEGORY: "category",
  PRODUCT_MAIN: "product-main",
  PRODUCT_DETAIL: "product-detail",
  BANNER: "banner",
} as const;
export type ImageUploadKind = (typeof IMAGE_UPLOAD_KIND)[keyof typeof IMAGE_UPLOAD_KIND];

export const imageUploadKindSchema = z.enum([
  IMAGE_UPLOAD_KIND.CATEGORY,
  IMAGE_UPLOAD_KIND.PRODUCT_MAIN,
  IMAGE_UPLOAD_KIND.PRODUCT_DETAIL,
  IMAGE_UPLOAD_KIND.BANNER,
]) satisfies z.ZodType<ImageUploadKind>;
